import React, { memo, ReactNode, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import DraggableList from 'src/presentation/components/pages/shared/DraggableList';
import { NextData } from 'src/models/Sidekick/SidekickThreads';
import { StyledItem } from '../ThreadBuilder';
import {
  selectAnon,
  selectCurrentAnonModifier,
  selectCurrentModifier,
  selectCurrentModule,
  selectCurrentModulesState,
  selectModuleSpecsMap,
} from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import ErrorComponent from '../ErrorComponent';
import FocusedContainer from 'src/presentation/components/pages/sidekick/threads/components/FocusedContainer';
import ModifierActions, { ModifierActionsContainer } from './ModifierActions';
import ConnectorWrapper from '../Connectors/ConnectorWrapper';
import { Collapsed } from 'src/presentation/components/agent_desktop_components';
import { StyledContent } from '@ad-scripts/presentation/components/common/collapse';

const DraggableModifiers = ({
  attr,
  disabled,
  module,
  onModifierClick,
}: {
  attr: string;
  disabled: boolean;
  module: NextData;
  onModifierClick;
}) => {
  const modifiers = module.modifierValues;

  const modulesState = useSelector(selectCurrentModulesState);
  const state = modulesState[module.id!];

  const modifiersContainerRef = useRef<HTMLDivElement | null>(null);

  return (
    <StyledCollapsed isActive={state?.isOpen}>
      <DraggableList
        className="draggable-modifiers"
        ref={modifiersContainerRef}
        disabled={disabled}
        items={modifiers}
        renderRow={props => (
          <ModifierDraggableItem
            attr={`${attr}[${props.index}]`}
            {...props}
            disabled={disabled}
            module={module}
            onModifierClick={onModifierClick}
          />
        )}
        droppableId={`${module.id}`}
        type="modifier"
      />
    </StyledCollapsed>
  );
};

const StyledCollapsed = styled(Collapsed)`
  border: 0;
  border-radius: 0;

  ${StyledContent} {
    padding: 0;
  }
`;

const ModifierDraggableItem = ({ attr, disabled, module, item: modifier, provided, onModifierClick }) => {
  const currenModifier = useSelector(selectCurrentModifier);
  const currentModule = useSelector(selectCurrentModule);
  const isAnonModule = useSelector(selectAnon);
  const currenAnonModifier = useSelector(selectCurrentAnonModifier);

  const isSelected = currenModifier?.id === modifier.id || currenAnonModifier?.id === modifier.id;

  const moduleSpecsMap = useSelector(selectModuleSpecsMap);

  const moduleSpec = moduleSpecsMap[module.name];
  const modifierSpec = moduleSpec?.modifiers?.find(mod => {
    return mod.name === modifier.name;
  });

  const moduleDisplayName = modifierSpec?.presentableName || modifier.name;
  const name =
    modifier.name === 'rule'
      ? `Add Rule${modifier?.controlValues?.label?.trim() ? `: ${modifier?.controlValues?.label}` : ''}`
      : moduleDisplayName;

  const handleModifierClick = evn => {
    evn.preventDefault();
    if (isSelected) {
      return;
    }
    onModifierClick && onModifierClick(modifier);
  };

  const modifierId = useMemo(
    () => (isAnonModule ? currenModifier.id : modifier.id),
    [isAnonModule, currenModifier, modifier]
  );

  if (!modifierId) {
    return null;
  }

  return (
    <FocusedContainer role="listitem">
      <ModifierItem modifierId={modifierId} moduleId={module.id}>
        <StyleModifierItem
          className="modifier-item"
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={handleModifierClick}
          isSelected={isSelected}
          {...provided.dragHandleProps}
        >
          <StyledItem>{name}</StyledItem>
          <ErrorComponent attr={attr} />
          <ModifierActions
            anonModifierId={isAnonModule ? modifier.id : null}
            disabled={disabled}
            modifierId={isAnonModule ? currenModifier.id : modifier.id}
            modifier={modifier}
            moduleId={isAnonModule ? currentModule.id : module.id}
          />
        </StyleModifierItem>
      </ModifierItem>
    </FocusedContainer>
  );
};

const ModifierItem = ({
  children,
  modifierId,
  moduleId,
}: {
  children: ReactNode;
  modifierId: string;
  moduleId: string;
}) => {
  const meta = { moduleId };

  return (
    <ConnectorWrapper connectedPointPosition="right" id={modifierId} type="MODIFIER" meta={meta}>
      {children}
    </ConnectorWrapper>
  );
};

const StyleModifierItem = styled.div.attrs(p => ({ isSelected: p.isSelected || false }))`
  align-items: center;
  display: flex;
  height: 50px;
  padding: 8px 12px 8px 44px;
  position: relative;

  background-color: ${p => (p.isSelected ? p.theme.colors.green100 : p.theme.colors.white)};

  ${p => p.isSelected && `background-color: ${p.theme.colors.green100}`};
  border: 1px solid transparent;

  &:hover {
    border: 1px solid ${p => p.theme.colors.green400};

    ${ModifierActionsContainer} {
      visibility: visible;
    }
  }
`;

export default memo(DraggableModifiers);
